import React, { useEffect, useState } from 'react';
import { useLocale } from '../context/LocaleContext';
import { isPreviewLanguage } from '../i18n/languages';
import styles from '../styles/languageSwitcher.module.css';

function compactLanguageLabel(code: string, preview: boolean): string {
  const base = code.split('-')[0].toUpperCase();
  return preview ? `${base}*` : base;
}

function optionLabel(
  lang: { code: string; nativeName: string; name: string },
  compact: boolean,
  previewSuffix: string
): string {
  const preview = isPreviewLanguage(lang.code);
  if (compact) {
    return compactLanguageLabel(lang.code, preview);
  }
  if (preview) {
    return `${lang.nativeName} (${previewSuffix})`;
  }
  return lang.nativeName;
}

/**
 * Language control for header — each language in its own name (English / Español).
 * Hidden when English is the only language this visitor can choose.
 */
const LanguageSwitcher: React.FC = () => {
  const { locale, setLocale, selectableLanguages, t } = useLocale();
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setCompact(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  if (selectableLanguages.length <= 1) return null;

  const previewSuffix = t('i18n.switcher.preview');

  return (
    <label className={styles.wrap}>
      <span className={styles.visuallyHidden}>{t('i18n.switcher.aria')}</span>
      <select
        className={compact ? `${styles.select} ${styles.selectCompact}` : styles.select}
        value={locale}
        aria-label={t('i18n.switcher.aria')}
        title={t('i18n.switcher.label')}
        onChange={(e) => void setLocale(e.target.value)}
      >
        {selectableLanguages.map((lang) => (
          <option key={lang.code} value={lang.code} title={lang.name}>
            {optionLabel(lang, compact, previewSuffix)}
          </option>
        ))}
      </select>
    </label>
  );
};

export default LanguageSwitcher;
